import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import { handleLogout, openAccountLink } from '@/utils/linksUtils';
import k12Logo from "@assets/images/k12_logo.svg";
import StriderClock from "@assets/images/strider-clock.svg";
import StriderWriting from "@assets/images/strider-writing.svg";
import { useLinksContext } from "@/context/LinksContext";
import config from "@/config";
import ErrorContent from "@/components/ErrorContent/ErrorContent";

const ErrorScreen: React.FC = () => {
  const location = useLocation();
  const state = location.state as { errorType?: string } | undefined;
  const errorType = state?.errorType;

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  let title = "";
  let message: React.ReactNode = null;
  let image = "";
  const { oktaAuth } = useOktaAuth();
  const { myAccountLink } = useLinksContext();

  const genericUnavailableMessage = (
    <>
      <p>This content is currently unavailable. Please try reloading the page.</p>
      <p className="mt-0">
        Need more help? Try{" "}
        <a
          href={config.HELP_CENTER_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          contacting support
        </a>
        .
      </p>
    </>
  );

  switch (errorType) {
    case "DPoP":
    case "NONCE":  
      title = "Please update your system clock";
      message = (
        <>
          <p>
            It looks like your device is set to an incorrect time. This causes the website to have trouble loading correctly.
          </p>
          <p>Here are instructions for updating your system clock automatically:</p>
          <ul>
            <li><a href='https://support.apple.com/en-in/guide/mac-help/mchlp2996/mac' target="_blank">Mac</a></li>
            <li><a href='https://support.microsoft.com/en-us/windows/set-time-date-and-time-zone-settings-in-windows-dfaa7122-479f-5b98-2a7b-fa0b6e01b261' target="_blank">Windows</a></li>
            <li><a href='https://support.google.com/chromebook/answer/177871?hl=en' target="_blank">Chromebook</a></li>
            <li><a href='https://support.apple.com/en-us/101619' target="_blank">iPhone</a></li>
            <li><a href='https://support.google.com/android/answer/2841106?hl=en' target="_blank">Android</a></li>
          </ul>
          <p className="mt-0">
            Need more help? Try{" "}
            <a
              href={config.HELP_CENTER_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              contacting support
            </a>
            .
          </p>
        </>
      );
      image = StriderClock;
    break;

    case "USER_API":
      title = "Something went wrong!";
      message = genericUnavailableMessage;
      image = StriderWriting;
    break;

    case undefined:
      title = "Something went wrong!";
      message = genericUnavailableMessage;
      image = StriderWriting;
    break;
  }

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-3 text-primary fw-medium">
          Please wait while we are accessing your account....
        </div>
      </div>
    );
  }
  
  return (
    
    <div className="error-page" data-type={errorType}>
      <div className="error-header">
        <div className="logo">
          <img src={k12Logo} />
        </div>
        <div className="error-top-nav-quicklinks">
          <div className="col d-flex justify-content-end">
              
          <a href="#" 
              onClick={(e) => {
              e.preventDefault();
              openAccountLink(myAccountLink);
              }} 
              className="nav-link"
              id="logout">Account
          </a>  
          &nbsp;&nbsp;|&nbsp;&nbsp; 
          
          <a href="#" 
              onClick={async (e) => {
                  e.preventDefault();
                  await handleLogout(oktaAuth, config.oidc.postLogoutRedirectUri);
              }}
              id="logout"
              className="nav-link">Logout
          </a>
          </div>
      </div>
      </div>
      <ErrorContent title={title} message={message} image={image} />
      <div className="error-footer">
        <div className="footer-left">
          <a href="https://www.k12.com/terms-of-use/" target="_blank">Terms</a>
          <a href="https://www.stridelearning.com/privacy-policy/" target="_blank">Privacy</a>
          <a href="https://www.k12.com/ip-policy/" target="_blank">Copyright</a>
          <a href="https://www.k12.com/accessibility/" target="_blank">Accessibility</a>
        </div>
        <p className="copyright-text">
          Copyright &copy; {new Date().getFullYear()} Stride, Inc. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ErrorScreen;